import React, { useRef, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonItem,
  IonLabel,
  IonInput,
  IonText,
  IonIcon,
  IonList,
  IonSegment,
  IonSegmentButton,
  useIonRouter,
  useIonViewWillLeave,
} from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { personOutline, idCardOutline, chatboxOutline } from 'ionicons/icons';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useUserContext } from '@/modules/user/useUserContext';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import {
  ProfileWizardLogic,
  PRONOUN_OPTIONS,
  NO_PRONOUNS_VALUE,
  RETURN_BUTTON_LABEL_PARAM,
} from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';
import { footerControlButtonStyle } from '@/modules/config/constants';
import styles from './AttributionStep.module.css';

// An item lays its slotted children out in a column — label above control —
// only when it can see an `ion-input` to infer a stacked label from. The pronoun
// group's control is a segment, so it asks for that layout by name, using the
// same host class Ionic sets on the other two items itself.
const STACKED_ITEM_CLASS = 'item-label-stacked';

// "they/them" → "they-them", so each choice gets a selector-safe test id.
const pronounTestId = (value: string) =>
  `profile-pronouns-${value.replace('/', '-')}`;

// The text a name input shows while the user types. UserContext keeps only the
// trimmed name, so the input keeps its own copy (which still has the space just
// typed before the next word) and writes every keystroke through to the
// context. The copy follows the context whenever the context changes to a name
// the copy doesn't trim to, such as when the stored name is restored, and drops
// its own surrounding whitespace on blur.
const useNameInput = (
  stored: string | undefined,
  store: (value: string) => void
) => {
  const [shown, setShown] = useState(stored ?? '');
  const [lastStored, setLastStored] = useState(stored);
  if (stored !== lastStored) {
    setLastStored(stored);
    if ((stored ?? '') !== shown.trim()) setShown(stored ?? '');
  }
  return {
    value: shown,
    onInput: (value: string) => {
      setShown(value);
      store(value);
    },
    onBlur: () => setShown(shown.trim()),
  };
};

const AttributionStep: React.FC = () => {
  // Attribution values have no pending/verify semantics, so they live directly
  // in UserContext (write-through on every keystroke) rather than in wizard state.
  const {
    familiarName: contextFamiliarName,
    setFamiliarName,
    allowedName: contextAllowedName,
    setAllowedName,
    preferredPronouns: contextPreferredPronouns,
    setPreferredPronouns,
  } = useUserContext();
  const { navigateWithOrigin, navigateToStep, progress } =
    useProfileWizardContext();
  const router = useIonRouter();
  const location = useLocation();
  // The page's measurements differ by Ionic mode (see AttributionStep.module.css);
  // the mode class hands them to everything inside as custom properties.
  const { isIonModeIOS } = useDeviceInfo();
  const allowedNameRef = useRef<HTMLIonInputElement>(null);

  const familiarNameInput = useNameInput(contextFamiliarName, setFamiliarName);
  const allowedNameInput = useNameInput(contextAllowedName, setAllowedName);
  // An optional caller-supplied label (via the `returnButtonLabel` query param)
  // for a button that returns the user straight to where they came from — e.g.
  // a share button they were sent here to unlock. The caller always reaches this
  // step via a single 'push', so router.goBack() lands back on that page. It is
  // always enabled: leaving without a name is fine, since the action stays
  // guarded until one is given. The label is written in sentence case, like
  // every footer button label.
  const queryString = router.routeInfo?.search || location.search;
  const returnButtonLabel =
    new URLSearchParams(queryString).get(RETURN_BUTTON_LABEL_PARAM) ?? '';
  // With a way back on offer, the rest of the profile is a detour the user
  // can take or leave, and Next says so.
  const nextLabel = returnButtonLabel
    ? 'Optional: set up the rest of your profile'
    : 'Next';
  // "set up" is a verb here, which title-casing can't tell from "up" the
  // preposition.
  const nextTitleCase = returnButtonLabel
    ? 'Optional: Set Up the Rest of Your Profile'
    : undefined;

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // Only the familiar name is required; the allowed name and the pronouns are
  // the sender's to withhold.
  const canAdvance = Boolean(contextFamiliarName);

  // enterkeyhint only relabels the return key; the navigation is ours to wire.
  // "next" on Familiar Name advances focus to Allowed Name (Ionic's setFocus
  // reaches the inner native input); "done" on Allowed Name dismisses the
  // keyboard by blurring the focused field.
  const handleFamiliarNameKeyDown = (
    e: React.KeyboardEvent<HTMLIonInputElement>
  ) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    void allowedNameRef.current?.setFocus();
  };

  const handleAllowedNameKeyDown = (
    e: React.KeyboardEvent<HTMLIonInputElement>
  ) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // Everything the step collects is stored before either exit button is
  // pressed: the names write through on every keystroke and the pronouns on
  // selection. That leaves the exit handlers pure navigation on purpose — a
  // UserContext write in the same tick as the return button's cross-outlet
  // goBack() re-evaluates the share guards mid-transition and wedges the
  // returning share-wizard page blank.

  const handleNext = () => {
    navigateWithOrigin('/profile/steps/phone', 'forward');
  };

  const handleReturn = () => {
    // The caller pushed this step onto history, so goBack returns to it. (A push
    // to an explicit path fails silently here: the profile wizard's nested router
    // outlet can't navigate to a sibling root route like /topics/….)
    router.goBack();
  };

  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('attribution')}
        progress={progress}
        onBack={() =>
          navigateToStep(ProfileWizardLogic.previousStep('attribution'), 'back')
        }
      />
      <IonContent>
        <div
          className={`${styles.page} ${
            isIonModeIOS ? styles.modeIOS : styles.modeMD
          }`}
        >
          <IonText>
            <p className={styles.intro}>
              How do you want to identify yourself in Snowball messages?
            </p>
          </IonText>
          <IonList>
            <IonItem className={styles.field}>
              <IonLabel position="stacked">
                <IonIcon icon={personOutline} className={styles.fieldIcon} />
                Familiar Name
              </IonLabel>
              <IonText color="medium">
                <p className={styles.fieldHelp}>
                  Available whenever you send a message to your contacts, e.g.
                  &ldquo;Hey, this is &lt;Familiar Name&gt;. I&apos;m just
                  reaching out to...&rdquo;
                </p>
              </IonText>
              <IonInput
                value={familiarNameInput.value}
                onIonInput={e =>
                  familiarNameInput.onInput((e.detail.value as string) || '')
                }
                onIonBlur={familiarNameInput.onBlur}
                placeholder="e.g. Alex Rivera"
                autocapitalize="words"
                autocomplete="off"
                autocorrect="off"
                spellcheck={false}
                enterkeyhint="next"
                onKeyDown={handleFamiliarNameKeyDown}
                data-testid="profile-familiar-name"
              />
            </IonItem>
          </IonList>
          <div className={styles.optionalSection}>
            <h2 className={styles.sectionTitle}>Optional</h2>
            <IonText color="medium">
              <p
                className={styles.sectionHelp}
                data-testid="profile-optional-help"
              >
                How Snowball can mention you when composing a reshared message
                with attribution, e.g. &ldquo;&lt;Allowed Name&gt; said
                &lt;She&gt; was coming, too.&rdquo; Feel free to skip these and
                we&apos;ll just use &ldquo;someone&rdquo; or a similarly generic
                reference.
              </p>
            </IonText>
          </div>
          <IonList>
            <IonItem className={styles.field}>
              <IonLabel position="stacked">
                <IonIcon icon={idCardOutline} className={styles.fieldIcon} />
                Allowed Name
              </IonLabel>
              <IonText color="medium">
                <p className={styles.fieldHelp}>Short name or initials</p>
              </IonText>
              <IonInput
                ref={allowedNameRef}
                value={allowedNameInput.value}
                onIonInput={e =>
                  allowedNameInput.onInput((e.detail.value as string) || '')
                }
                onIonBlur={allowedNameInput.onBlur}
                placeholder="e.g. AR"
                autocapitalize="words"
                autocomplete="off"
                autocorrect="off"
                spellcheck={false}
                enterkeyhint="done"
                onKeyDown={handleAllowedNameKeyDown}
                data-testid="profile-allowed-name"
              />
            </IonItem>
            <IonItem
              className={`${STACKED_ITEM_CLASS} ${styles.field} ${styles.fieldGroup}`}
              lines="none"
            >
              <IonLabel position="stacked">
                <IonIcon icon={chatboxOutline} className={styles.fieldIcon} />
                Pronouns
              </IonLabel>
              <IonSegment
                // Pronouns are optional, so "None" is selected until the user
                // picks a set, and choosing it again clears them.
                value={contextPreferredPronouns ?? NO_PRONOUNS_VALUE}
                onIonChange={e => {
                  const value = e.detail.value as string;
                  setPreferredPronouns(
                    value === NO_PRONOUNS_VALUE ? undefined : value
                  );
                }}
                data-testid="profile-pronouns"
                className={styles.pronounSegment}
              >
                {PRONOUN_OPTIONS.map(option => (
                  <IonSegmentButton
                    key={option.value}
                    value={option.value}
                    data-testid={pronounTestId(option.value)}
                    className={styles.segmentButton}
                  >
                    <IonLabel className={styles.segmentLabel}>
                      {option.label}
                    </IonLabel>
                  </IonSegmentButton>
                ))}
              </IonSegment>
            </IonItem>
          </IonList>
        </div>
      </IonContent>
      <FooterDock>
        {returnButtonLabel && (
          <FooterDockRow>
            <SmartCaseButton
              expand="block"
              fill="outline"
              onClick={handleReturn}
              color="primary"
              className={`footer-glass-button ${styles.footerButton}`}
              data-testid="profile-attribution-return-button"
              // Frosted "liquid glass" fill (not-quite opaque + backdrop blur)
              // so scrolling page text stays legible behind the outline
              // button's otherwise-transparent interior instead of jumbling
              // with its label. The default outline border/text still read as
              // secondary next to the "Next" action. It stays inline: the
              // custom properties are shared with the app's other dock buttons.
              style={{ ...footerControlButtonStyle } as React.CSSProperties}
            >
              {returnButtonLabel}
            </SmartCaseButton>
          </FooterDockRow>
        )}
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            onClick={handleNext}
            disabled={!canAdvance}
            color="primary"
            className={styles.footerButton}
            data-testid="profile-wizard-next"
            titleCase={nextTitleCase}
          >
            {nextLabel}
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>
    </IonPage>
  );
};

export default AttributionStep;
